import React from 'react';
import css from './MediaMargin.module.scss';
import editorCss from './ScriptEditor.module.scss';
import type { MediaLabel } from '../viewModel/mediaLabels';
import { NAME_GAP_MARKER } from '../viewModel/publicNames';
import type { NameGap } from '../viewModel/publicNames';

/** An empty line still occupies a row, in the textarea and so in this margin. */
const BLANK_ROW = String.fromCharCode(0x200b);

/** P3: the action beside a label, with the scene's actual name. */
export function openForEditing(sceneName: string | null): string {
  return `Open ${sceneName || 'this scene'} for editing`;
}

/**
 * T20-02: the disclosure itself, as the row shows it. One short word, because it is the one thing
 * on the row that never gives way, and a fixed width is only fixed if the word inside it is.
 */
export const SHOW_LINE_LABELS = 'Show';

/**
 * T19-04, corrected by T20-02: what the disclosure is called where its name has room, on hover and
 * to a screen reader.
 *
 * A row of this margin is exactly as tall as its script line, so every label on a row stands on one
 * line of a narrow column and the long ones are cut short to fit. They are all still here, and all
 * still read aloud; this is how a writer sees the whole of any of them, and of the one on a row
 * that carries only one, which a narrow margin cuts short exactly as readily.
 */
export function showAllLabels(count: number): string {
  return count === 1
    ? 'Show the label on this line in full'
    : `Show all ${count} labels on this line in full`;
}

export type MediaMarginProps = {
  /** The script's logical lines, in order: the margin takes its row heights from them. */
  lines: string[];
  /** The labels for each 1-based line, from the view model. */
  labelsByLine: Map<number, MediaLabel[]>;
  /**
   * T17-06, sized by T18-04: the missing-name MARKER, keyed by the line of the label it is about.
   *
   * The guidance used to sit in the Suggestions panel, which is collapsed on open, so something a
   * writer has to act on was behind a fold. It belongs beside the label it names — but this margin
   * positions each row's box against the script's own row height, so a paragraph in here hangs down
   * over the rows below it. What stands beside the label is therefore a one-line marker; the
   * sentence it stands for is in the guidance panel, where it has space of its own (`NameNotes`).
   */
  notesByLine?: Map<number, NameGap[]>;
  /** Ask to open a scene. The margin never leaves on its own; the surface asks the writer first. */
  onOpenScene?: (branchId: number) => void;
  /** T19-04: ask for every label on a line, which the surface shows in a panel of its own. */
  onShowLine?: (line: number) => void;
  /** The rows element, lifted by the same transform as the overlay and the gutter. */
  rowsRef?: React.RefObject<HTMLDivElement>;
  /** A wheel over the margin belongs to the script. */
  onWheel?: (event: React.WheelEvent<HTMLDivElement>) => void;
};

function MediaMarginComponent(props: MediaMarginProps) {
  const {
    lines,
    labelsByLine,
    notesByLine,
    onOpenScene,
    onShowLine,
    rowsRef,
    onWheel,
  } = props;

  return (
    <div className={css.margin} onWheel={onWheel} aria-label="Media labels">
      <div className={css.rows} ref={rowsRef} data-testid="basil-media-rows">
        {lines.map((line, index) => {
          const number = index + 1;
          const labels = labelsByLine.get(number) ?? [];
          const notes = notesByLine?.get(number) ?? [];
          const scene = labels.length > 0 ? labels[0].sceneName : null;
          const branchId = labels.length > 0 ? labels[0].branchId : null;

          return (
            <div className={css.row} key={`media-${number}`} data-line={number}>
              <span className={editorCss.gutterWidthMirror} aria-hidden="true">
                {line === '' ? BLANK_ROW : line}
              </span>
              {labels.length > 0 || notes.length > 0 ? (
                <span className={css.stack}>
                  {labels.length > 0 ? (
                    <span className={css.labels} data-testid={`basil-media-${number}`}>
                      {labels.map((label) => (
                        <span
                          className={css.label}
                          data-kind={label.kind}
                          title={label.text}
                          key={`${label.kind}-${label.text}`}
                        >
                          {label.text}
                        </span>
                      ))}
                      {branchId != null && onOpenScene ? (
                        <button
                          type="button"
                          className={css.open}
                          onClick={() => onOpenScene(Number(branchId))}
                        >
                          {openForEditing(scene)}
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                  {/* T20-02: OUTSIDE the group that clips, so a row too narrow to show its labels
                      still shows the way to them. The labels shrink around this; this never
                      shrinks. Every row that carries a label carries it, the one-label rows too:
                      one long label is cut short by a narrow margin exactly as three are. */}
                  {labels.length > 0 && onShowLine ? (
                    <button
                      type="button"
                      className={css.more}
                      data-testid={`basil-media-more-${number}`}
                      title={showAllLabels(labels.length)}
                      aria-label={showAllLabels(labels.length)}
                      onClick={() => onShowLine(number)}
                    >
                      {SHOW_LINE_LABELS}
                    </button>
                  ) : null}
                  {notes.length > 0 ? (
                    <span className={css.notes} data-testid={`basil-name-marker-${number}`}>
                      {notes.map((note) => (
                        <span className={css.marker} key={note.domain}>
                          {NAME_GAP_MARKER[note.domain]}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Basil v0.2, P3 — the episode's pictures, video and sound, as read-only facts beside the script.
 *
 * It is built exactly as the left gutter is: one row per LOGICAL line, each row carrying an
 * invisible copy of its line at the text column's width so a wrapped line pushes its own label down
 * with it, and the whole strip lifted by the same transform as the overlay. That is what keeps a
 * label on the line it is about.
 *
 * T19-04: and the row is therefore the budget. A row is exactly as tall as its script line, so
 * anything in it taller than that line is over the NEXT line, which is somebody else's. The labels
 * wrapped, the exit control came after them and the missing-name marker after that, and three
 * groups stacked in a box pinned only by its top edge went wherever they liked.
 *
 * The stack is now pinned on all four edges, which makes the row the budget rather than a
 * suggestion, and everything in it stands on ONE line: the labels, the disclosure that reads them
 * in full, the exit, the marker. Nothing wraps and nothing stacks.
 *
 * T20-02: and WHERE a thing stands on that line decides whether it can be taken away. The labels
 * give way first, cut short with an ellipsis and their whole text on their `title`, inside a group
 * that shrinks and clips as a whole. The disclosure was a child of that group, so a narrow enough
 * margin clipped the way out along with the labels it was the way to. It is a sibling of the group
 * now, with a fixed width the shrinking cannot reach, and every row carrying any label carries it,
 * a row with one label included. What it opens is `MediaDetail`, a panel in the flow above the
 * script holding that line's labels in full and the scene's protected exit. The exit in the margin
 * stays where it is, a shortcut inside the group that gives way, because the exit a writer must be
 * able to reach is the one in the panel.
 *
 * Nothing here is editable and nothing here is in the textarea. The labels state what the episode
 * has; the one control under them is the protected exit into the scene's own editor, and the
 * surface — not this component — decides whether it may be taken.
 */
export const MediaMargin = React.memo(MediaMarginComponent);
